import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguageStore } from '../store/languageStore';

const faqs = [
  {
    id: 1,
    question: {
      EN: "What services does RightNowAid provide?",
      ES: "¿Qué servicios ofrece RightNowAid?",
      ZH: "RightNowAid提供哪些服务？",
      VI: "RightNowAid cung cấp những dịch vụ gì?",
      FIL: "Ano ang mga serbisyo na inaalok ng RightNowAid?",
      KO: "RightNowAid는 어떤 서비스를 제공하나요?",
      AR: "ما هي الخدمات التي يقدمها RightNowAid؟"
    },
    answer: {
      EN: "RightNowAid connects individuals with essential services including food assistance, housing support, legal aid, and healthcare resources. We work with trusted partners to provide comprehensive support to those in need.",
      ES: "RightNowAid conecta a las personas con servicios esenciales, incluyendo asistencia alimentaria, apoyo de vivienda, asistencia legal y recursos de salud. Trabajamos con socios confiables para brindar apoyo integral a quienes lo necesitan.",
      ZH: "RightNowAid将个人与基本服务联系起来，包括食品援助、住房支持、法律援助和医疗资源。我们与值得信赖的合作伙伴合作，为有需要的人提供全面支持。",
      VI: "RightNowAid kết nối cá nhân với các dịch vụ thiết yếu bao gồm hỗ trợ thực phẩm, hỗ trợ nhà ở, trợ giúp pháp lý và tài nguyên y tế. Chúng tôi làm việc với các đối tác đáng tin cậy để cung cấp hỗ trợ toàn diện cho những người có nhu cầu.",
      FIL: "Ang RightNowAid ay kumokonekta sa mga indibidwal sa mga mahahalagang serbisyo kabilang ang food assistance, housing support, legal aid, at healthcare resources. Nakikipagtulungan kami sa mga mapagkakatiwalaang partner para magbigay ng komprehensibong suporta sa mga nangangailangan.",
      KO: "RightNowAid는 식품 지원, 주거 지원, 법률 지원 및 의료 자원을 포함한 필수 서비스를 개인에게 연결합니다. 우리는 신뢰할 수 있는 파트너와 협력하여 필요한 사람들에게 포괄적인 지원을 제공합니다.",
      AR: "يربط RightNowAid الأفراد بالخدمات الأساسية بما في ذلك المساعدة الغذائية والدعم السكني والمساعدة القانونية وموارد الرعاية الصحية. نحن نعمل مع شركاء موثوقين لتقديم دعم شامل لمن يحتاجون."
    }
  },
  {
    id: 2,
    question: {
      EN: "How can I get help from RightNowAid?",
      ES: "¿Cómo puedo obtener ayuda de RightNowAid?",
      ZH: "如何从RightNowAid获得帮助？",
      VI: "Làm thế nào để nhận được sự giúp đỡ từ RightNowAid?",
      FIL: "Paano ako makakakuha ng tulong mula sa RightNowAid?",
      KO: "RightNowAid에서 어떻게 도움을 받을 수 있나요?",
      AR: "كيف يمكنني الحصول على المساعدة من RightNowAid؟"
    },
    answer: {
      EN: "Getting help is simple. Visit our website, select your preferred language, and follow the step-by-step guide to find the services you need. Our system will connect you with the most relevant resources in your area.",
      ES: "Obtener ayuda es simple. Visite nuestro sitio web, seleccione su idioma preferido y siga la guía paso a paso para encontrar los servicios que necesita. Nuestro sistema lo conectará con los recursos más relevantes en su área.",
      ZH: "获得帮助很简单。访问我们的网站，选择您喜欢的语言，然后按照分步指南找到您需要的服务。我们的系统将把您与您所在地区最相关的资源联系起来。",
      VI: "Nhận được sự giúp đỡ rất đơn giản. Truy cập trang web của chúng tôi, chọn ngôn ngữ ưa thích của bạn và làm theo hướng dẫn từng bước để tìm các dịch vụ bạn cần. Hệ thống của chúng tôi sẽ kết nối bạn với các tài nguyên phù hợp nhất trong khu vực của bạn.",
      FIL: "Madaling makakuha ng tulong. Bisitahin ang aming website, piliin ang iyong gustong wika, at sundan ang step-by-step guide para mahanap ang mga serbisyong kailangan mo. Ikokonekta ka ng aming sistema sa mga pinaka-angkop na resources sa iyong lugar.",
      KO: "도움을 받는 것은 간단합니다. 웹사이트를 방문하고 선호하는 언어를 선택한 다음 필요한 서비스를 찾기 위한 단계별 가이드를 따르세요. 우리 시스템은 귀하의 지역에서 가장 관련성 높은 리소스와 연결해 드립니다.",
      AR: "الحصول على المساعدة أمر بسيط. قم بزيارة موقعنا الإلكتروني، واختر لغتك المفضلة، واتبع الدليل خطوة بخطوة للعثور على الخدمات التي تحتاجها. سيقوم نظامنا بتوصيلك بالموارد الأكثر صلة في منطقتك."
    }
  },
  {
    id: 3,
    question: {
      EN: "Is RightNowAid available in my area?",
      ES: "¿Está RightNowAid disponible en mi área?",
      ZH: "RightNowAid在我的地区可用吗？",
      VI: "RightNowAid có sẵn trong khu vực của tôi không?",
      FIL: "Available ba ang RightNowAid sa aking lugar?",
      KO: "RightNowAid가 내 지역에서 이용 가능한가요?",
      AR: "هل RightNowAid متاح في منطقتي؟"
    },
    answer: {
      EN: "RightNowAid is continuously expanding its network of service providers. Enter your location on our website to see available services in your area. If we don't have coverage yet, we'll help you find alternative resources.",
      ES: "RightNowAid está expandiendo continuamente su red de proveedores de servicios. Ingrese su ubicación en nuestro sitio web para ver los servicios disponibles en su área. Si aún no tenemos cobertura, lo ayudaremos a encontrar recursos alternativos.",
      ZH: "RightNowAid正在不断扩大其服务提供商网络。在我们的网站上输入您的位置，查看您所在地区的可用服务。如果我们还没有覆盖，我们将帮助您找到替代资源。",
      VI: "RightNowAid đang không ngừng mở rộng mạng lưới nhà cung cấp dịch vụ. Nhập vị trí của bạn trên trang web của chúng tôi để xem các dịch vụ có sẵn trong khu vực của bạn. Nếu chúng tôi chưa có phạm vi phủ sóng, chúng tôi sẽ giúp bạn tìm các tài nguyên thay thế.",
      FIL: "Ang RightNowAid ay patuloy na pinalalawak ang network ng mga service provider. Ilagay ang iyong lokasyon sa aming website para makita ang available na mga serbisyo sa iyong lugar. Kung wala pa kaming coverage, tutulungan ka naming makahanap ng alternative resources.",
      KO: "RightNowAid는 서비스 제공자 네트워크를 지속적으로 확장하고 있습니다. 웹사이트에 위치를 입력하여 해당 지역의 이용 가능한 서비스를 확인하세요. 아직 커버리지가 없다면 대체 리소스를 찾는 데 도움을 드리겠습니다.",
      AR: "RightNowAid يقوم بتوسيع شبكة مزودي الخدمة باستمرار. أدخل موقعك على موقعنا الإلكتروني لرؤية الخدمات المتاحة في منطقتك. إذا لم يكن لدينا تغطية بعد، فسن帮助你 في العثور على موارد بديلة."
    }
  }
];

export const FAQ = () => {
  const { selectedLanguage } = useLanguageStore();
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-16 sm:py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {selectedLanguage === "EN" ? "Frequently Asked Questions" :
             selectedLanguage === "ES" ? "Preguntas Frecuentes" :
             selectedLanguage === "ZH" ? "常见问题" :
             selectedLanguage === "VI" ? "Câu Hỏi Thường Gặp" :
             selectedLanguage === "FIL" ? "Mga Madalas Itanong" :
             selectedLanguage === "KO" ? "자주 묻는 질문" :
             "الأسئلة الشائعة"}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {selectedLanguage === "EN" ? "Find answers to common questions about our services and how we can help you." :
             selectedLanguage === "ES" ? "Encuentre respuestas a preguntas comunes sobre nuestros servicios y cómo podemos ayudarlo." :
             selectedLanguage === "ZH" ? "查找有关我们服务以及我们如何帮助您的常见问题的答案。" :
             selectedLanguage === "VI" ? "Tìm câu trả lời cho các câu hỏi thường gặp về dịch vụ của chúng tôi và cách chúng tôi có thể giúp bạn." :
             selectedLanguage === "FIL" ? "Hanapin ang mga sagot sa mga karaniwang tanong tungkol sa aming mga serbisyo at kung paano ka namin matutulungan." :
             selectedLanguage === "KO" ? "우리 서비스와 도움을 받는 방법에 대한 일반적인 질문에 대한 답변을 찾으세요." :
             "ابحث عن إجابات للأسئلة الشائعة حول خدماتنا وكيف يمكننا مساعدتك."}
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: faq.id * 0.1 }}
              className="border border-border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-card hover:bg-accent/5 transition-colors"
              >
                <span className="font-medium text-foreground">
                  {faq.question[selectedLanguage]}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-muted-foreground transition-transform ${
                    openId === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <AnimatePresence>
                {openId === faq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="px-6 py-4 bg-card/50"
                  >
                    <p className="text-muted-foreground">
                      {faq.answer[selectedLanguage]}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}; 